import { useState, useEffect } from "react";
import { useDioptraFetch } from "./use-dioptra-fetch";
import { fetchCustomProperty } from "../utils/document";
import { fetchDocName } from "../utils/document";
import { readDocumentWithinUploadLimit } from "../utils/document-upload-guard";
import { getEnv } from "../utils/envManager";
import { useAnalytics } from "../context/AnalyticsContext";
import type { IcertisUploadRequest } from "@common/schemas/icertis";

type IcertisUploadOutcome = "uploaded" | "too_large";

export const useIcertisUpload = () => {
    const [canUpload, setCanUpload] = useState<boolean>(false);
    const [disabledReason, setDisabledReason] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { dioptraFetch } = useDioptraFetch();
    const { analytics } = useAnalytics();

    useEffect(() => {
        const checkProperties = async () => {
            try {
                setIsLoading(true);
                const [type, entityName, sysId, version] = await Promise.all([
                    fetchCustomProperty("Type"),
                    fetchCustomProperty("EntityName"),
                    fetchCustomProperty("SysId"),
                    fetchCustomProperty("Version"),
                ]);

                const allPropertiesExist = type !== null && entityName !== null && sysId !== null && version !== null;
                const isAgreement = type === "Agreement";

                setCanUpload(allPropertiesExist && isAgreement);

                if (!allPropertiesExist) {
                    setDisabledReason("This document was not opened from Icertis");
                } else if (!isAgreement) {
                    setDisabledReason("Upload is only available for Agreement documents");
                } else {
                    setDisabledReason(null);
                }
            } catch (error) {
                console.error("Error checking custom properties:", error);
                setCanUpload(false);
                setDisabledReason("Unable to check document properties");
            } finally {
                setIsLoading(false);
            }
        };

        checkProperties();
    }, []);

    async function uploadToIcertis(note: string): Promise<IcertisUploadOutcome> {
        const [type, entityName, sysId] = await Promise.all([
            fetchCustomProperty("Type"),
            fetchCustomProperty("EntityName"),
            fetchCustomProperty("SysId"),
        ]);

        if (!type || !entityName || !sysId || type !== "Agreement") {
            throw new Error("Required Icertis properties are missing or invalid");
        }

        const documentBase64 = await readDocumentWithinUploadLimit();
        if (documentBase64 === null) {
            return "too_large";
        }

        const documentName = await fetchDocName();
        const filename = documentName || "document.docx";
        const extension = filename.split(".").pop() || "docx";

        const apiUrl = `${getEnv("WEBAPP_API_BASE_URL")}/api/integrations/icertis/agreements/${entityName}/${sysId}/upload`;
        const uploadRequest: IcertisUploadRequest = {
            fileContent: documentBase64,
            fileName: filename,
            fileExtension: extension,
            note: note.trim() || undefined,
        };

        await dioptraFetch(apiUrl, {
            method: "POST",
            body: JSON.stringify(uploadRequest),
        });
        analytics?.track("icertis_document_uploaded", {
            agreementId: sysId,
            contractTypeName: entityName,
            hasNote: uploadRequest.note !== undefined,
        });
        return "uploaded";
    }

    return { canUpload, disabledReason, isLoading, uploadToIcertis };
};
